import { LinkText } from '@/components/ui/link-text';
import { useToast } from '@/components/ui/use-toast';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { Dialog } from '@/features/dialog/dialog';
import { DialogForm } from '@/features/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import { useDrawers } from '@/features/drawers/all-drawers';
import { useVoiceRecording } from '@/lib/contexts/voice-recording-context';
import { useAppState } from '@/lib/hooks/use-app-state';
import { useLocale } from '@/lib/hooks/use-locale';
import { plainTextToLexicalSync } from '@/lib/rich-text/plain-text-to-lexical';
import type { CrmTaskDTO } from '@/server/dto/crm-task-dto';
import type { CrmTaskInputDTO } from '@/server/dto/crm-task-input-dto';
import { crmTaskInputDtoSchema } from '@/server/dto/crm-task-input-dto';
import { api } from '@/utils/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { CrmTaskStatus } from '@plato/data/prisma';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { TaskDescriptionField } from './task-description-field';
import { TaskFormFields } from './task-form-fields';

type TraskCreateDialogProps = {
    onClose?: () => void;
    taskId?: string;
    links?: Pick<CrmTaskDTO['links'][0], 'modelId' | 'modelType' | 'title'>[];
    description?: string;
};

export const TraskCreateDialog = DialogStateManager.create<TraskCreateDialogProps>(
    ({ links, onClose, description }: TraskCreateDialogProps) => {
        const { visible, remove } = useDialog();
        const appState = useAppState();
        const sendEvent = useAnalytics();
        const { t } = useLocale('tasks');
        const { toast } = useToast();
        const dialog = useDialog();
        const apiUtils = api.useUtils();
        const { currentSessionId } = useVoiceRecording();
        const { openTaskDetails } = useDrawers();

        const defaultValues = useMemo(() => {
            return {
                dueDate: new Date(),
                links: links || [],
                assignedToId: appState.platoUser?.id || '',
                assignedById: appState.platoUser?.id || '',
                createdById: appState.platoUser?.id || '',
                status: CrmTaskStatus.OPEN,
                description: description ? (plainTextToLexicalSync(description) ?? undefined) : undefined,
            };
        }, [appState.platoUser?.id, links, description]);

        const form = useForm<CrmTaskInputDTO>({
            defaultValues,
            resolver: zodResolver(crmTaskInputDtoSchema),
        });

        const {
            handleSubmit,
            formState: { isValid: isFormValid },
        } = form;

        const createTaskMutation = api.platoUser.command.tasks.postTasksActions.create.useMutation({
            onSuccess(data: CrmTaskDTO) {
                apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate();
                apiUtils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.invalidate();
                apiUtils.platoUser.query.tasks.getManyTasks.count.invalidate();
                apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate();
                apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate();

                if (data.dueDate) {
                    sendEvent('task_created', {
                        task_id: data.taskId,
                        due_date: data.dueDate.toISOString(),
                        assignee_id: data.assignedTo.id,
                        assignor_id: data.assignedBy.id,
                        is_assigned_to_oneself: data.assignedTo.id
                            ? data.assignedTo.id === data.assignedBy.id
                            : false,
                        related_entity_type: data.links[0]?.modelType,
                        related_entity_id: data.links[0]?.modelId,
                    });
                }

                toast({
                    icon: 'success',
                    title: t('tasks__toast__created_success_title'),
                    createdLink: <LinkText as="button" onClick={() => openTaskDetails(data.taskId)} />,
                });

                dialog.remove();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const onSubmit = (values: CrmTaskInputDTO) => {
            createTaskMutation.mutate(values);
        };

        return (
            <Dialog
                showClose
                open={visible}
                onClose={() => {
                    remove();
                    onClose?.();
                }}
            >
                <DialogForm>
                    <DialogForm.Header title={t('tasks__form_create_title')} />
                    <DialogForm.Content>
                        <DialogForm.ItemList>
                            <TaskDescriptionField form={form} />
                        </DialogForm.ItemList>
                        <TaskFormFields form={form} />
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        isLoading={createTaskMutation.isLoading}
                        onCancel={remove}
                        onSubmit={handleSubmit(onSubmit)}
                        disabled={!isFormValid || !!currentSessionId}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
